// /welcomedayeditor — the password-protected Welcome Day editor.
//
// Gated by the operator's ADMIN_PASSWORD (the password-only /api/auth door →
// admin token; APP_PASSWORD is retired since 2026-11-02). The token is
// held in sessionStorage and in the welcomeday api module, and sent as a Bearer
// header on saves + image uploads. On a fresh session the user re-enters the
// password once.

import { useState, useCallback } from 'react';
import WelcomeDayEditor from './welcomeday/WelcomeDayEditor.jsx';
import { createHttpDataSource } from './pathfinder/lib/dataSource.js';
import { setAuthToken } from './welcomeday/api.js';
import UMapsWordmark from './components/UMapsWordmark.jsx';

const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const dataSource = createHttpDataSource('/Map%20Info/pathfinder/');
const TOKEN_KEY = 'welcomeday_token';

export default function WelcomeDayEditorPage() {
  const [authed, setAuthed] = useState(() => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (t) setAuthToken(t);
    return !!t;
  });
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const login = useCallback(async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API_BASE}/api/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.token) {
        sessionStorage.setItem(TOKEN_KEY, d.token);
        setAuthToken(d.token);
        setAuthed(true);
      } else {
        setErr(d.error || 'Incorrect password.');
      }
    } catch {
      setErr('Login failed — is the server running?');
    } finally {
      setBusy(false);
    }
  }, [pw]);

  if (authed) {
    return (
      <div style={{ position: 'fixed', inset: 0 }}>
        <WelcomeDayEditor dataSource={dataSource} apiKey={MAPS_KEY} />
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#ecdcbb', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <form onSubmit={login} style={{
        background: '#fff', padding: '28px 26px', borderRadius: 16, width: 'min(360px, calc(100% - 32px))',
        boxShadow: '0 18px 50px rgba(90,70,30,0.35)', borderBottom: '4px solid #fcb40c',
      }}>
        <UMapsWordmark tool="Welcome Day Editor" size={26} href={null}
                       style={{ marginBottom: 6, color: '#431a0d', fontSize: 18 }} />
        <p style={{ margin: '0 0 16px', color: '#7c6a4c', fontSize: 13 }}>Enter the editor password to continue.</p>
        <input
          type="password" value={pw} onChange={e => setPw(e.target.value)} autoFocus
          placeholder="Password"
          style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px', fontSize: 15,
            border: '1px solid #d0d4e8', borderRadius: 10, marginBottom: 12 }}
        />
        {err && <div style={{ color: '#c5221f', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <button type="submit" disabled={busy || !pw} style={{
          width: '100%', padding: '11px 12px', fontSize: 15, fontWeight: 700, color: '#fff',
          background: busy || !pw ? '#b9a06a' : '#431a0d', border: 'none', borderRadius: 10,
          cursor: busy || !pw ? 'default' : 'pointer',
        }}>{busy ? 'Checking…' : 'Enter'}</button>
      </form>
    </div>
  );
}
